import { createContext, useContext, useEffect, useState } from "react";
import {
  apiSignIn,
  apiSignUp,
  apiSignOut,
  apiGetMe,
  doRefresh,
  getToken,
  getStoredUser,
  clearSession,
  updateStoredUser,
  isTokenExpired,
} from "../services/authService";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    async function restoreSession() {
      const token = getToken();
      const storedUser = getStoredUser();

      if (!token || !storedUser) {
        setAuthLoading(false);
        return;
      }

      if (!isTokenExpired(token)) {
        // Token still valid — use stored user immediately, no network call
        setUser(storedUser);
        setAuthLoading(false);
        return;
      }

      // Token expired — try refresh (throws if there's no refresh token or
      // the request fails, either way handled the same way below)
      try {
        await doRefresh();

        // Fetch fresh user data (username may have changed in future)
        const data = await apiGetMe();
        if (data?.user) {
          updateStoredUser(data.user);
          setUser(data.user);
        } else {
          clearSession();
        }
      } catch {
        clearSession();
      } finally {
        setAuthLoading(false);
      }
    }

    restoreSession();
  }, []);

  // Listen for forced sign-out triggered by authFetch on failed refresh
  useEffect(() => {
    function handleSignedOut() { setUser(null); }
    window.addEventListener("auth:signedout", handleSignedOut);
    return () => window.removeEventListener("auth:signedout", handleSignedOut);
  }, []);

  async function signIn(email, password) {
    const data = await apiSignIn(email, password); // throws on error, stores session
    setUser(data.user);
    return data;
  }

  async function signUp(username, email, password, acceptedPolicies = []) {
    const data = await apiSignUp(username, email, password, acceptedPolicies); // throws on error
    // No session yet — email confirmation required
    return data; // { pending: true, message, email }
  }

  async function signOut() {
    await apiSignOut(); // clears localStorage
    setUser(null);
  }

  function updateUser(patch) {
    setUser((prev) => {
      const next = { ...prev, ...patch };
      updateStoredUser(next);
      return next;
    });
  }

  return (
    <AuthContext.Provider value={{ user, authLoading, signIn, signUp, signOut, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
